import { DateTime } from 'luxon'

/*
  As variáveis das mensagens personalizadas. O salão as insere pelos botões do editor, e o texto
  salvo guarda o token em português ([nome_cliente]), que é o que ele lê e escreve. Cada token
  aponta para um dado do sistema pelo nome em inglês (`field`), e é por esse dado que a mensagem
  é preenchida, no cliente, na hora de montar o link do WhatsApp: a API guarda o texto como veio
  e não troca nada.

  `{customer_name}` é a variável de antes dos botões, digitada à mão. Mensagens salvas ainda a
  usam, então ela continua valendo, como sinônimo de [nome_cliente].

  Este arquivo é igual no cutilei-business-web-app, no cutilei-business-app e no
  cutilei-professional-app, porque os três enviam as mesmas mensagens. Mudou aqui, mude nos três.
*/

const TOKEN_PATTERN = /\[(\w+)\]|\{customer_name\}/g

const CUTILEI_PRICE_TEXT = 'incluso na assinatura'

const formatPrice = price => parseFloat(price).toLocaleString('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})

const getFirstName = name => name?.split(' ')[0] || ''

/*
  "Ana", "Ana e Bia", "Ana, Bia e Carla", sem vazios nem repetidos. À mão porque o Hermes dos
  apps não tem `Intl.ListFormat`.
*/
const joinNames = names => {
  const uniqueNames = [...new Set(names.filter(Boolean))]

  return uniqueNames.length > 1
    ? `${uniqueNames.slice(0, -1).join(', ')} e ${uniqueNames[uniqueNames.length - 1]}`
    : uniqueNames[0] || ''
}

/*
  O valor de cada dado, pelo nome em inglês. `schedules` são os agendamentos do cliente no dia, em
  ordem de horário: data, horário e tolerância são os do primeiro, que é quando o cliente chega;
  serviços, profissionais e preço cobrem o dia todo, como na mensagem de confirmação.
*/
const RESOLVERS = {
  'customer.name': ({ customer }) => getFirstName(customer?.name),

  'business.name': ({ schedules }) => schedules[0]?.business?.name,

  /*
    O nome que a confirmação, a agenda e os e-mails da API mostram. O apelido do serviço no
    salão não vem no agendamento.
  */
  'service.name': ({ schedules }) => joinNames(schedules.map(schedule => schedule.service?.name)),

  'schedule.date': ({ schedules }) => (
    schedules[0]?.date ? DateTime.fromISO(schedules[0].date).toFormat('dd/MM/yyyy') : ''
  ),

  'schedule.time': ({ schedules }) => schedules[0]?.time,

  'professional.nickname': ({ schedules }) => joinNames(schedules.map(schedule => (
    schedule.professional?.nickname || getFirstName(schedule.professional?.name)
  ))),

  /*
    No agendamento Cutilei a assinante não paga no salão, e o `price` dele é o que a Cutilei
    repassa: fica fora da soma, como na confirmação, e sozinho vira o texto da assinatura.
  */
  'schedule.price': ({ schedules }) => {
    const pricedSchedules = schedules.filter(schedule => !schedule.cutilei_schedule)

    if (pricedSchedules.length === 0) return schedules.length > 0 ? CUTILEI_PRICE_TEXT : ''

    const total = pricedSchedules.reduce((sum, schedule) => sum + (parseFloat(schedule.price) || 0), 0)
    return `R$${formatPrice(total)}`
  },

  'schedule.max_delay': ({ schedules }) => {
    const maxDelay = schedules[0]?.max_delay

    if (maxDelay === null || maxDelay === undefined) return ''
    return `${maxDelay} ${maxDelay === 1 ? 'minuto' : 'minutos'}`
  }
}

export default class CustomMessageService {
  static VARIABLES = Object.freeze([
    { key: 'nome_cliente', label: 'Nome do cliente', field: 'customer.name' },
    { key: 'nome_salao', label: 'Nome do salão', field: 'business.name' },
    { key: 'servico', label: 'Serviço', field: 'service.name' },
    { key: 'data', label: 'Data', field: 'schedule.date' },
    { key: 'horario', label: 'Horário', field: 'schedule.time' },
    { key: 'nome_profissional', label: 'Nome do profissional', field: 'professional.nickname' },
    { key: 'preco', label: 'Preço', field: 'schedule.price' },
    { key: 'tolerancia_atraso', label: 'Tolerância de atraso', field: 'schedule.max_delay' }
  ].map(variable => Object.freeze({ ...variable, token: `[${variable.key}]` })))

  static CUTILEI_PRICE_TEXT = CUTILEI_PRICE_TEXT

  static formatPrice = formatPrice

  /*
    Uma passada só, com função na troca: o dado inserido nunca é relido como variável, e um
    `R$12` no dado não vira referência de grupo. Token desconhecido fica como está, e dado
    ausente vira texto vazio. `{customer_name}` não tem grupo, por isso cai em nome_cliente.
  */
  static fillVariables(content, { customer, schedules = [] }) {
    return (content ?? '').replace(TOKEN_PATTERN, (token, key = 'nome_cliente') => {
      const variable = CustomMessageService.VARIABLES.find(variable => variable.key === key)
      return variable ? (RESOLVERS[variable.field]({ customer, schedules }) ?? '') : token
    })
  }
}
